/**
 * Layer 5 — map lettering and city symbols.
 *
 *  - City markers (town dot / city square / metropolis block / capital star) as instanced
 *    sprites with a gently clamped screen size.
 *  - City names in EB Garamond small caps with a paper halo; region names in spaced italic
 *    capitals at low zoom. Labels keep a near-constant screen size, and a greedy priority
 *    placement (capitals > metropolises > regions > cities > towns) hides colliding labels;
 *    visibility changes fade.
 *  - The sheet furniture: theatre title cartouche and a graduated scale bar in the margin.
 *  - Lettering follows the interface language: province epithets, the theatre title and the
 *    German names of real towns ("Warschau") are re-set when it changes (i18n/names.ts);
 *    generated city names are proper names and never change.
 *  - Neutral countries of real-world theatres (closed land) are lettered in upright spaced grey
 *    capitals at every zoom, like the foreign countries in the margin of a staff map.
 *  - The scale bar follows the theatre's kilometres per hex (`GameMap.hexKm`, else 10 km).
 *  - Night glow of cities (additive, lives in the lit layer above the day/night multiply).
 */
import { type GameMap, HEX_W, type HexGrid, NO_FACTION } from '@friction/engine';
import { Container, Text, TextStyle, type Texture } from 'pixi.js';
import { getLocale, type Locale, tr } from '../../i18n';
import { cityName, regionName, theatreTitle } from '../../i18n/names';
import type { MarkerGlyphs } from '../atlas/glyphs';
import type { Camera } from '../camera';
import { SERIF, STENCIL } from '../fonts';
import { LineBatch } from '../gfx/lines';
import { QuadBatch, Scale } from '../gfx/quads';
import { cssColor, PAL } from '../palette';
import type { SheetLayout } from '../sheet';

interface Label {
  text: Text;
  x: number;
  y: number;
  /** Unscaled size (css px at label scale 1). */
  w: number;
  h: number;
  /** Horizontal offset from the anchor in css px (right of the marker). */
  dx: number;
  priority: number;
  minZoom: number;
  maxZoom: number;
  alpha: number;
  target: number;
  kind: 'city' | 'region' | 'country';
  /** Marker half-size in css px (city labels), used as a collision obstacle. */
  marker: number;
  /** The engine's name (regions are re-lettered per language). */
  name: string;
}

const HALO = { color: 0xf1e8d0, alpha: 0.88, width: 3.4, join: 'round' as const };

const cityStyle = (size: number, weight: '500' | '600' | '700') =>
  new TextStyle({
    fontFamily: SERIF,
    fontSize: size,
    fontWeight: weight,
    fontVariant: 'small-caps',
    letterSpacing: size * 0.04,
    fill: cssColor(PAL.ink),
    stroke: HALO,
    padding: 4,
  });

/** Marker css-px scale per layout unit for a zoom (gentle clamp around 1). */
export const markerScale = (zoom: number): number => Math.min(1.35, Math.max(0.62, 0.55 + zoom * 0.45));
/** Label css-px scale for a zoom. */
const labelScale = (zoom: number): number => Math.min(1.3, Math.max(0.78, 0.7 + zoom * 0.3));

export class LabelsLayer {
  readonly container = new Container({ label: 'labels' });
  /** Additive night glow; the renderer mounts it above the lighting pass. */
  readonly glowMesh: QuadBatch['mesh'];
  private readonly markers: QuadBatch;
  private readonly glow: QuadBatch;
  private readonly textLayer = new Container({ label: 'label-text' });
  private readonly furniture = new Container({ label: 'sheet-furniture' });
  private readonly scaleBar = new LineBatch('scale-bar', 256);
  private readonly labels: Label[] = [];
  private readonly map: GameMap;
  private readonly grid: HexGrid;
  private readonly glyphs: MarkerGlyphs;
  private layoutVersion = -1;
  private layoutAt = 0;
  private lastZoom = 0;
  private flicker = new Float32Array(0);
  private locale: Locale = getLocale();
  private title: Text | null = null;
  private sub: Text | null = null;
  private sheetNo: Text | null = null;

  constructor(
    map: GameMap,
    grid: HexGrid,
    sheet: SheetLayout,
    glyphs: MarkerGlyphs,
    atlas: Texture,
    resolution: number,
  ) {
    this.map = map;
    this.grid = grid;
    this.glyphs = glyphs;
    this.markers = new QuadBatch('city-markers', atlas, 512);
    this.glow = new QuadBatch('city-glow', atlas, 512);
    this.glow.mesh.blendMode = 'add';
    this.glowMesh = this.glow.mesh;
    const res = resolution * 1.6;

    for (const city of map.cities) {
      const capital = city.capitalOf !== NO_FACTION;
      const size = capital ? 17 : city.size === 3 ? 16 : city.size === 2 ? 13.5 : 11.5;
      const weight = capital ? '700' : city.size === 3 ? '600' : '500';
      const text = new Text({
        text: cityName(city.name, this.locale),
        style: cityStyle(size, weight),
        resolution: res,
        anchor: { x: 0, y: 0.5 },
      });
      const marker = capital ? 9 : city.size === 3 ? 7.5 : city.size === 2 ? 5.6 : 4.2;
      this.labels.push({
        text,
        x: grid.cx(city.hex),
        y: grid.cy(city.hex),
        w: text.width,
        h: text.height * 0.7,
        dx: marker + 3,
        priority: capital ? 100 + city.size : city.size === 3 ? 80 : city.size === 2 ? 60 : 20,
        minZoom: capital ? 0 : city.size === 3 ? 0.28 : city.size === 2 ? 0.5 : 0.9,
        maxZoom: 99,
        alpha: 0,
        target: 0,
        kind: 'city',
        marker,
        name: city.name,
      });
    }
    for (const region of map.regions) {
      if (region.hex < 0 || region.hex >= grid.count) continue;
      if (map.initialOwner[region.hex] === NO_FACTION) {
        this.labels.push(this.countryLabel(region, grid, res));
        continue;
      }
      const text = new Text({
        text: regionName(region.name, this.locale).toUpperCase(),
        style: new TextStyle({
          fontFamily: SERIF,
          fontSize: 17,
          fontStyle: 'italic',
          fontWeight: '500',
          letterSpacing: 6,
          fill: cssColor(0x4a4034),
          stroke: { ...HALO, alpha: 0.55, width: 3 },
          padding: 4,
        }),
        resolution: res,
        anchor: 0.5,
      });
      this.labels.push({
        text,
        x: grid.cx(region.hex),
        y: grid.cy(region.hex),
        w: text.width,
        h: text.height * 0.8,
        dx: 0,
        priority: 50,
        minZoom: 0,
        maxZoom: 0.95,
        alpha: 0,
        target: 0,
        kind: 'region',
        marker: 0,
        name: region.name,
      });
    }
    this.labels.sort((a, b) => b.priority - a.priority);
    for (const l of this.labels) {
      l.text.visible = false;
      this.textLayer.addChild(l.text);
    }
    this.flicker = new Float32Array(map.cities.length).map((_, i) => ((i * 2654435761) >>> 0) / 4294967296);
    this.buildFurniture(sheet, resolution);
    this.buildMarkers();
    this.container.addChild(this.furniture, this.markers.mesh, this.textLayer);
  }

  /** A neutral country's name: always shown, never faded out by zoom. */
  private countryLabel(region: { name: string; hex: number }, grid: HexGrid, res: number): Label {
    const text = new Text({
      text: regionName(region.name, this.locale).toUpperCase(),
      style: new TextStyle({
        fontFamily: SERIF,
        fontSize: 19,
        fontWeight: '600',
        letterSpacing: 7,
        fill: cssColor(0x5b5750),
        stroke: { ...HALO, alpha: 0.6, width: 3 },
        padding: 4,
      }),
      resolution: res,
      anchor: 0.5,
    });
    return {
      text,
      x: grid.cx(region.hex),
      y: grid.cy(region.hex),
      w: text.width,
      h: text.height * 0.8,
      dx: 0,
      priority: 55,
      minZoom: 0,
      maxZoom: 99,
      alpha: 0,
      target: 0,
      kind: 'country',
      marker: 0,
      name: region.name,
    };
  }

  private buildMarkers(): void {
    const m = this.markers;
    const g = this.glyphs;
    m.clear();
    const cities = [...this.map.cities].sort((a, b) => a.size - b.size);
    for (const c of cities) {
      const capital = c.capitalOf !== NO_FACTION;
      const glyph = capital ? g.capital : c.size === 3 ? g.metropolis : c.size === 2 ? g.city : g.town;
      m.push(
        this.grid.cx(c.hex),
        this.grid.cy(c.hex),
        0,
        0,
        glyph.w,
        glyph.h,
        glyph.frame,
        0xffffff,
        1,
        Scale.Marker,
      );
    }
    m.commit();
  }

  private buildFurniture(sheet: SheetLayout, resolution: number): void {
    const f = sheet.frame;
    const t = tr(this.locale);
    const title = new Text({
      text: theatreTitle(this.map.name, this.locale).toUpperCase(),
      style: new TextStyle({
        fontFamily: SERIF,
        fontSize: 46,
        fontWeight: '600',
        letterSpacing: 9,
        fill: cssColor(PAL.ink),
      }),
      resolution: resolution * 3,
    });
    title.position.set(f.x0, f.y0 - 118);
    const sub = new Text({
      text: t('map.furniture'),
      style: new TextStyle({ fontFamily: STENCIL, fontSize: 17, letterSpacing: 3, fill: cssColor(0x5a4c3a) }),
      resolution: resolution * 3,
    });
    sub.position.set(f.x0 + 2, f.y0 - 52);
    const sheetNo = new Text({
      text: t('map.sheet', { n: ((this.map.seed >>> 0) % 89) + 10 }),
      style: new TextStyle({
        fontFamily: SERIF,
        fontSize: 22,
        fontStyle: 'italic',
        fill: cssColor(0x5a4c3a),
      }),
      resolution: resolution * 3,
      anchor: { x: 1, y: 0 },
    });
    sheetNo.position.set(f.x1, f.y0 - 52);
    this.furniture.addChild(title, sub, sheetNo);
    this.title = title;
    this.sub = sub;
    this.sheetNo = sheetNo;

    // scale bar: five blocks of a round distance, alternating ink / paper
    const b = this.scaleBar;
    const x0 = f.x0;
    const y = f.y1 + 44;
    const hexKm = this.map.hexKm ?? 10;
    const step = hexKm <= 12 ? 10 : hexKm <= 25 ? 20 : hexKm <= 60 ? 50 : 100;
    const seg = (HEX_W * step) / hexKm;
    b.add([x0, y, x0 + seg * 5, y], { width: 7, color: PAL.ink, alpha: 0.9 });
    for (let i = 0; i < 5; i++) {
      if (i % 2)
        b.add([x0 + seg * i + 1.2, y, x0 + seg * (i + 1) - 1.2, y], {
          width: 4.6,
          color: PAL.paper,
          alpha: 1,
        });
    }
    b.commit();
    this.furniture.addChild(b.mesh);
    for (let i = 0; i <= 5; i++) {
      const t = new Text({
        text: i === 5 ? tr(this.locale)('map.scale', { km: 5 * step }) : `${i * step}`,
        style: new TextStyle({ fontFamily: SERIF, fontSize: 15, fill: cssColor(PAL.ink) }),
        resolution: resolution * 3,
        anchor: { x: i === 5 ? 0.2 : 0.5, y: 1 },
      });
      t.position.set(x0 + seg * i, y - 8);
      this.furniture.addChild(t);
    }
  }

  /** Greedy decluttering in screen space. */
  private layout(cam: Camera): void {
    const zoom = cam.zoom;
    const ls = labelScale(zoom);
    const ms = markerScale(zoom);
    const placed: number[] = [];
    const hit = (x0: number, y0: number, x1: number, y1: number) => {
      for (let i = 0; i < placed.length; i += 4) {
        if (x0 < placed[i + 2] && x1 > placed[i] && y0 < placed[i + 3] && y1 > placed[i + 1]) return true;
      }
      return false;
    };
    // markers of cities whose names can show are obstacles for other labels
    for (const l of this.labels) {
      if (l.kind !== 'city' || zoom < l.minZoom * 0.8) continue;
      const [sx, sy] = cam.worldToScreen(l.x, l.y);
      const r = l.marker * ms;
      placed.push(sx - r, sy - r, sx + r, sy + r);
    }
    const markerCount = placed.length;
    for (const l of this.labels) {
      l.target = 0;
      if (zoom < l.minZoom || zoom > l.maxZoom) continue;
      const [sx, sy] = cam.worldToScreen(l.x, l.y);
      const s = l.kind === 'city' ? ls : Math.min(1.25, Math.max(0.85, 1.35 - zoom * 0.6));
      const w = l.w * s;
      const h = l.h * s;
      const x0 = l.kind === 'city' ? sx + l.dx * ms : sx - w / 2;
      const y0 = sy - h / 2;
      if (x0 > cam.screenW || x0 + w < 0 || y0 > cam.screenH || y0 + h < 0) continue;
      // a label may overlap its own marker only
      if (
        hit(x0 - 2, y0 - 1, x0 + w + 2, y0 + h + 1) &&
        !(l.kind === 'city' && onlyOwnMarker(placed, markerCount, sx, sy, x0, y0, w, h))
      )
        continue;
      placed.push(x0 - 2, y0 - 1, x0 + w + 2, y0 + h + 1);
      l.target = 1;
    }
  }

  /** Per frame: scales, fades, and (throttled) re-layout when the camera moved. */
  /** Re-letter the sheet in another language (province epithets, theatre title, margin notes). */
  private relabel(locale: Locale): void {
    this.locale = locale;
    const t = tr(locale);
    for (const l of this.labels) {
      if (l.kind === 'city') {
        l.text.text = cityName(l.name, locale);
        l.w = l.text.width;
        continue;
      }
      l.text.text = regionName(l.name, locale).toUpperCase();
      l.w = l.text.width;
      l.h = l.text.height * 0.8;
    }
    if (this.title) this.title.text = theatreTitle(this.map.name, locale).toUpperCase();
    if (this.sub) this.sub.text = t('map.furniture');
    if (this.sheetNo) this.sheetNo.text = t('map.sheet', { n: ((this.map.seed >>> 0) % 89) + 10 });
    this.layoutVersion = -1;
  }

  update(
    cam: Camera,
    resolution: number,
    dt: number,
    now: number,
    night: number,
    reducedMotion: boolean,
  ): void {
    if (getLocale() !== this.locale) this.relabel(getLocale());
    const zoom = cam.zoom;
    const ms = markerScale(zoom);
    this.markers.setUnits(ms / zoom, ms / zoom);
    this.scaleBar.setZoom(zoom, resolution);
    if (
      cam.version !== this.layoutVersion &&
      (now - this.layoutAt > 90 || Math.abs(zoom / this.lastZoom - 1) > 0.08)
    ) {
      this.layoutVersion = cam.version;
      this.layoutAt = now;
      this.lastZoom = zoom;
      this.layout(cam);
    }
    const ls = labelScale(zoom);
    const k = Math.min(1, dt * 7);
    for (const l of this.labels) {
      l.alpha += (l.target - l.alpha) * k;
      if (Math.abs(l.target - l.alpha) < 0.01) l.alpha = l.target;
      const visible = l.alpha > 0.01;
      l.text.visible = visible;
      if (!visible) continue;
      const s = (l.kind === 'city' ? ls : Math.min(1.25, Math.max(0.85, 1.35 - zoom * 0.6))) / zoom;
      l.text.scale.set(s);
      l.text.alpha = l.alpha * (l.kind === 'region' ? Math.min(1, (0.95 - zoom) / 0.2) : 1);
      l.text.position.set(l.x + (l.dx * ms) / zoom, l.y);
    }
    this.buildGlow(night, now, reducedMotion);
  }

  private buildGlow(night: number, now: number, reducedMotion: boolean): void {
    const g = this.glow;
    g.clear();
    if (night > 0.02) {
      const t = now / 1000;
      this.map.cities.forEach((c, i) => {
        const f = reducedMotion ? 1 : 0.9 + 0.1 * Math.sin(t * (1.3 + this.flicker[i]) + i);
        const size = c.size === 3 ? 150 : c.size === 2 ? 105 : 64;
        g.push(
          this.grid.cx(c.hex),
          this.grid.cy(c.hex),
          0,
          0,
          size,
          size,
          this.glyphs.glow.frame,
          PAL.cityGlow,
          night * 0.5 * f,
          Scale.World,
        );
      });
    }
    g.commit();
  }

  destroy(): void {
    this.markers.destroy();
    this.glow.destroy();
    this.scaleBar.destroy();
    this.container.destroy({ children: true });
  }
}

/** True if the rect only collides with the marker at (sx, sy) (i.e. the label's own city). */
function onlyOwnMarker(
  placed: number[],
  markerCount: number,
  sx: number,
  sy: number,
  x0: number,
  y0: number,
  w: number,
  h: number,
): boolean {
  for (let i = 0; i < placed.length; i += 4) {
    if (
      !(
        x0 - 2 < placed[i + 2] &&
        x0 + w + 2 > placed[i] &&
        y0 - 1 < placed[i + 3] &&
        y0 + h + 1 > placed[i + 1]
      )
    )
      continue;
    const own =
      i < markerCount &&
      Math.abs((placed[i] + placed[i + 2]) / 2 - sx) < 0.5 &&
      Math.abs((placed[i + 1] + placed[i + 3]) / 2 - sy) < 0.5;
    if (!own) return false;
  }
  return true;
}
